import {useApiManager} from "../../../components/utils/api";
import {DirectoryPerson, EmpOrganizationRestControllerApiFactory, EmployeeRestControllerApiFactory, Item} from "arteco-api-client-ts";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	Link,
	Stack,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from "@mui/material";
import React, {ReactNode, useCallback, useEffect, useMemo, useState} from "react";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import {envVars} from "../../../components/utils/env_vars";
import {dateToString} from "../../../components/utils/dates";

const MONTHS = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

/** El directorio da el cumpleaños sin el año (#14002): «14 de marzo». */
const birthdayOf = (p: DirectoryPerson) =>
	p.birthdayDay && p.birthdayMonth ? `${p.birthdayDay} de ${MONTHS[p.birthdayMonth - 1]}` : "—";
import {
	btnSx,
	clampSx,
	EmptyState,
	FilterChips,
	ListCard,
	Page,
	PageHeader,
	PersonAvatar,
	SearchField,
	SkeletonRows,
	Toolbar,
} from "../../../components/kit";
import {PersonRole, RoleBadges, rolesByPerson} from "../../../components/layout/shared/management_roles";
import {OrgChartView} from "./org_chart";

const photoUrl = (p: DirectoryPerson) => envVars.apiBasePath + "/pub/team/photo/" + p.number + "?width=500";

const displayName = (p?: DirectoryPerson) => p ? `${p.name || ""} ${p.surname || ""}`.trim() : "";

/** Sin tildes ni mayúsculas, para buscar «jose» y encontrar «José». */
const normalize = (s?: string) => (s || "").toLowerCase().normalize("NFD").replace(/\p{M}/gu, "");

const Fact = ({label, children}: { label: string, children: ReactNode }) => (
	<Box>
		<Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
		<Typography variant="body2" component="div">{children}</Typography>
	</Box>
);

/**
 * Directorio de la plantilla: tarjetas con foto, búsqueda por nombre y filtro por mánager; o el
 * organigrama por unidades (#13897). En los dos, los roles del sistema de gestión de cada persona (#13896).
 */
export const EmployeeDirectoryPage = () => {

	const apiManager = useApiManager();
	const employeeApi = apiManager.factory(EmployeeRestControllerApiFactory);

	const [detailed, setDetailed] = useState<DirectoryPerson | undefined>(undefined);
	const [manager, setManager] = useState<string>("");
	const [managers, setManagers] = useState<Item[]>([]);
	const [people, setPeople] = useState<DirectoryPerson[]>();
	const [term, setTerm] = useState("");
	const [view, setView] = useState<"cards" | "chart">("chart");
	const [roles, setRoles] = useState<Map<number, PersonRole[]>>(new Map());

	useEffect(() => {
		employeeApi.getEmployeeDirectory(undefined).then(resp => {
			const newManagers: Item[] = [];
			resp.data.content?.forEach(p => {
				if (p.manager && p.manager.value && !newManagers.find(m => m.value == p.manager?.value)) {
					newManagers.push(p.manager);
				}
			});
			setManagers(newManagers.sort((p1: Item, p2: Item): number => p1.label?.localeCompare(p2.label || "") || 0));
			setPeople(resp.data.content as []);
		});
		apiManager.factory(EmpOrganizationRestControllerApiFactory).currentManagementRoles()
			.then(resp => setRoles(rolesByPerson(resp.data))).catch(() => undefined);
	}, []);

	const selectById = useCallback((id: number) => {
		const p = (people || []).find(x => x.number === id);
		if (p) {
			setDetailed(p);
		}
	}, [people]);

	const visible = useMemo(() => (people || []).filter(p =>
		(!manager || String(p.manager?.value) === manager)
		&& (!term || normalize(`${p.name} ${p.surname} ${p.coverPhrase || ""}`).includes(normalize(term)))
	), [people, manager, term]);

	return (
		<Page>
			<PageHeader title="Directorio"
						subtitle={people ? `${people.length} personas en la plantilla` : "La plantilla de Arteco"}
						actions={
							<ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}
											   aria-label="Vista del directorio">
								<ToggleButton value="cards" sx={{textTransform: "none", px: 1.5, gap: 0.75}}>
									<GridViewRoundedIcon fontSize="small"/>Personas
								</ToggleButton>
								<ToggleButton value="chart" sx={{textTransform: "none", px: 1.5, gap: 0.75}}>
									<AccountTreeRoundedIcon fontSize="small"/>Organigrama
								</ToggleButton>
							</ToggleButtonGroup>
						}/>

			{view === "chart" ? <OrgChartView onSelect={selectById}/> : <>
			<Toolbar
				left={managers.length > 0 && (
					<FilterChips value={manager} onChange={setManager} options={[
						{value: "", label: "Todos"},
						...managers.map(m => ({value: String(m.value), label: m.label})),
					]}/>
				)}
				right={<SearchField value={term} onChange={setTerm} placeholder="Buscar persona"/>}
			/>

			{!people ? (
				<SkeletonRows rows={3} height={180}/>
			) : visible.length === 0 ? (
				<EmptyState icon={<SearchOffRoundedIcon/>} title="Nadie coincide con la búsqueda"/>
			) : (
				<Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)"}, gap: 2}}>
					{visible.map(p => (
						<ListCard key={p.number} onClick={() => setDetailed(p)} sx={{p: 2.5}}>
							<Stack alignItems="center" textAlign="center" spacing={1}>
								<PersonAvatar name={displayName(p)} src={photoUrl(p)} size={88}/>
								<Typography variant="subtitle2" fontWeight={700} sx={{pt: 0.5}}>{displayName(p)}</Typography>
								<Typography variant="body2" color="text.secondary" sx={{...clampSx(2), minHeight: 40}}>
									{p.coverPhrase || "Sin presentación"}
								</Typography>
								<RoleBadges roles={p.number !== undefined ? roles.get(p.number) : undefined} justify="center"/>
								{p.email && (
									<Link href={"mailto:" + p.email} variant="caption" underline="hover" onClick={e => e.stopPropagation()}
										  sx={{maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
										{p.email}
									</Link>
								)}
							</Stack>
						</ListCard>
					))}
				</Box>
			)}
			</>}

			<Dialog open={detailed != undefined} onClose={() => setDetailed(undefined)} fullWidth maxWidth="sm"
					PaperProps={{sx: {borderRadius: 3}}}>
				{detailed && (
					<>
						<DialogContent sx={{pt: 3}}>
							<Stack direction={{xs: "column", sm: "row"}} spacing={2.5} alignItems={{xs: "center", sm: "flex-start"}}>
								<PersonAvatar name={displayName(detailed)} src={photoUrl(detailed)} size={96}/>
								<Box sx={{minWidth: 0, textAlign: {xs: "center", sm: "left"}}}>
									<Typography variant="h6" fontWeight={700}>{displayName(detailed)}</Typography>
									{detailed.coverPhrase && <Typography variant="body2" color="text.secondary">{detailed.coverPhrase}</Typography>}
									{detailed.number !== undefined && roles.get(detailed.number) && (
										<Box sx={{mt: 1}}>
											<RoleBadges roles={roles.get(detailed.number)} justify={undefined}/>
										</Box>
									)}
								</Box>
							</Stack>
							<Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr"}, gap: 2, mt: 3}}>
								<Fact label="Correo">
									{detailed.email ? <Link href={"mailto:" + detailed.email} underline="hover">{detailed.email}</Link> : "—"}
								</Fact>
								<Fact label="Mánager">{detailed.manager?.label || "—"}</Fact>
								<Fact label="En Arteco desde">{dateToString(detailed.hireDate) || "—"}</Fact>
								<Fact label="Cumpleaños">{birthdayOf(detailed)}</Fact>
								<Fact label="Formación máxima">{detailed.maxTrainingLevel?.label || "—"}</Fact>
							</Box>
							<Box sx={{mt: 2}}>
								<Fact label="Biografía">
									<Box sx={{whiteSpace: "pre-wrap"}}>{detailed.bio || "Sin biografía"}</Box>
								</Fact>
							</Box>
						</DialogContent>
						<DialogActions sx={{px: 3, pb: 2.5}}>
							<Button onClick={() => setDetailed(undefined)} sx={btnSx}>Cerrar</Button>
						</DialogActions>
					</>
				)}
			</Dialog>
		</Page>
	);
};
